import React, { useRef, useEffect, useState } from "react";
import * as THREE from "three";

export default function MusicVisualizer() {
  const mountRef = useRef(null);
  const audioRef = useRef(null);

  const analyserRef = useRef(null);
  const dataArrayRef = useRef(null);
  const audioCtxRef = useRef(null);
  const sourceRef = useRef(null);

  const seaRef = useRef(null);
  const sunRef = useRef(null);

  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [volume, setVolume] = useState(0.8);
  const [progress, setProgress] = useState(0);

  /* ================= SCENE ================= */

  useEffect(() => {
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x87ceeb);

    const camera = new THREE.PerspectiveCamera(
      45,
      window.innerWidth / window.innerHeight,
      0.1,
      600
    );

    camera.position.set(0, 28, 45);
    camera.lookAt(0, 0, -40);

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    mountRef.current.appendChild(renderer.domElement);

    /* ================= LIGHT ================= */

    scene.add(new THREE.AmbientLight(0xffffff, 0.55));

    const sunLight = new THREE.DirectionalLight(0xffffff, 1.8);
    sunLight.position.set(0, 60, 20);
    scene.add(sunLight);

    /* ================= SUN ================= */

    const sun = new THREE.Mesh(
      new THREE.SphereGeometry(4.5, 64, 64),
      new THREE.MeshStandardMaterial({
        color: 0xffcc66,
        emissive: 0xffdd88,
        emissiveIntensity: 1.7
      })
    );

    sun.position.set(0, -3.5, -60);
    scene.add(sun);
    sunRef.current = sun;

    /* ================= SEA ================= */

    const seaGeometry = new THREE.PlaneGeometry(200, 120, 320, 240);
    seaGeometry.rotateX(-Math.PI / 2);

    const seaMaterial = new THREE.MeshPhysicalMaterial({
      color: 0x4db8ff,
      roughness: 0.18,
      metalness: 0.05,
      transparent: true,
      opacity: 0.9,
      reflectivity: 1,
      clearcoat: 0.45,
      clearcoatRoughness: 0.12
    });

    const sea = new THREE.Mesh(seaGeometry, seaMaterial);
    sea.position.set(0, 0, -10);
    scene.add(sea);
    seaRef.current = sea;

    /* ================= ANIMATION ================= */

    const clock = new THREE.Clock();

    const animate = () => {
      requestAnimationFrame(animate);
      const t = clock.getElapsedTime();

      /* --- Sun rise over 12 minutes --- */
      const p = Math.min(t / (12 * 60), 1);
      sun.position.y = THREE.MathUtils.lerp(-3.5, 42, p);
      sun.position.z = THREE.MathUtils.lerp(-60, -20, p);

      /* --- Waves --- */
      if (analyserRef.current && seaRef.current) {
        analyserRef.current.getByteFrequencyData(dataArrayRef.current);

        const bass =
          dataArrayRef.current.slice(0, 40).reduce((a, b) => a + b, 0) / 40;
        const mids =
          dataArrayRef.current.slice(40, 140).reduce((a, b) => a + b, 0) / 100;
        const highs =
          dataArrayRef.current.slice(140, 256).reduce((a, b) => a + b, 0) / 116;

        const pos = seaRef.current.geometry.attributes.position;

        for (let i = 0; i < pos.count; i++) {
          const x = pos.getX(i);
          const z = pos.getZ(i);

          if (z < -40) {
            pos.setY(i, 0);
            continue;
          }

          const depthFactor = THREE.MathUtils.clamp(
            (z + 40) / 40,
            0,
            1
          );

          const wave1 =
            Math.sin(x * 0.05 + t * 0.9) *
            bass * 0.018 *
            depthFactor;

          const wave2 =
            Math.cos(z * 0.08 + t * 1.2) *
            mids * 0.012 *
            depthFactor;

          const wave3 =
            Math.sin((x + z) * 0.15 + t * 2.0) *
            highs * 0.006 *
            depthFactor;

          pos.setY(i, wave1 + wave2 + wave3);
        }

        pos.needsUpdate = true;
        seaRef.current.geometry.computeVertexNormals();
      }

      renderer.render(scene, camera);
    };

    animate();

    return () => {
      mountRef.current.removeChild(renderer.domElement);
      renderer.dispose();
    };
  }, []);

  /* ================= AUDIO ================= */

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    audioRef.current.src = URL.createObjectURL(file);

    audioCtxRef.current = new AudioContext();
    sourceRef.current = audioCtxRef.current.createMediaElementSource(audioRef.current);
    analyserRef.current = audioCtxRef.current.createAnalyser();
    analyserRef.current.fftSize = 1024;

    dataArrayRef.current = new Uint8Array(analyserRef.current.frequencyBinCount);

    sourceRef.current.connect(analyserRef.current);
    analyserRef.current.connect(audioCtxRef.current.destination);
  };

  const play = async () => {
    await audioCtxRef.current.resume();
    audioRef.current.play();
    setIsPlaying(true);
  };

  const pause = () => {
    audioRef.current.pause();
    setIsPlaying(false);
  };

  /* ================= RECORDING ================= */

  const startRecording = () => {
    const canvasStream = mountRef.current.querySelector("canvas").captureStream(60);
    const audioStream = audioRef.current.captureStream();

    const combined = new MediaStream([
      ...canvasStream.getVideoTracks(),
      ...audioStream.getAudioTracks()
    ]);

    mediaRecorderRef.current = new MediaRecorder(combined, {
      mimeType: "video/webm; codecs=vp9,opus"
    });

    recordedChunksRef.current = [];
    mediaRecorderRef.current.ondataavailable = e => e.data.size && recordedChunksRef.current.push(e.data);
    mediaRecorderRef.current.onstop = download;

    mediaRecorderRef.current.start();
    setIsRecording(true);
  };

  const stopRecording = () => {
    mediaRecorderRef.current.stop();
    setIsRecording(false);
  };

  const download = () => {
    const blob = new Blob(recordedChunksRef.current, { type: "video/webm" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "lofi-ocean-sunrise.webm";
    a.click();
  };

  /* ================= UI ================= */

  return (
    <div>
      <div ref={mountRef} />

      <div
        style={{
          position: "fixed",
          bottom: 20,
          left: "50%",
          transform: "translateX(-50%)",
          background: "rgba(0,0,0,0.65)",
          padding: 16,
          borderRadius: 12,
          display: "flex",
          gap: 12,
          alignItems: "center",
          color: "#fff"
        }}
      >
        <input type="file" accept="audio/*" onChange={handleFile} />

        {!isPlaying ? (
          <button onClick={play}>▶ Play</button>
        ) : (
          <button onClick={pause}>⏸ Pause</button>
        )}

        <button onClick={startRecording} disabled={isRecording}>⏺ Record</button>
        <button onClick={stopRecording} disabled={!isRecording}>⏹ Stop</button>

        <input
          type="range"
          min="0"
          max="1"
          step="0.01"
          value={volume}
          onChange={(e) => {
            setVolume(e.target.value);
            audioRef.current.volume = e.target.value;
          }}
        />

        <audio
          ref={audioRef}
          onTimeUpdate={() =>
            setProgress(audioRef.current.currentTime / audioRef.current.duration)
          }
        />

        <input
          type="range"
          min="0"
          max="1"
          step="0.001"
          value={progress}
          onChange={(e) => {
            audioRef.current.currentTime =
              e.target.value * audioRef.current.duration;
          }}
        />
      </div>
    </div>
  );
}
